import React, { useState, useEffect } from "react";
import { 
  Sparkles, 
  Heart, 
  Coins, 
  Brain, 
  Activity, 
  Flower2, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  BookOpen, 
  PenTool, 
  Smartphone, 
  ChevronDown, 
  ChevronUp, 
  Menu, 
  X, 
  ArrowRight,
  User,
  Mail,
  Compass,
  Check,
  Award,
  AlertTriangle
} from "lucide-react";

export default function App() {
  const CHECKOUT_URL = "https://neurocoach.sneharath.com/web/checkout/6ac654cafcb24b5042f88c12";

  // Navigation State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setSetScrolled] = useState(false);

  // Struggles Checkboxes State
  const [struggles, setStruggles] = useState({
    financial: false,
    consistency: false,
    overthinking: false,
    wantMore: false,
    noDirection: false,
    forgetSelf: false,
    repeatingPatterns: false,
    peaceCost: false
  });

  // Curriculum Active Tab
  const [activeTab, setActiveTab] = useState(1);

  // FAQ Expanded Items
  const [faqExpanded, setFaqExpanded] = useState({});

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", whatsapp: "", agreeTime: false });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [redirectTimer, setRedirectTimer] = useState(4);

  // Urgency Timer State
  const [timeLeft, setTimeLeft] = useState({ hours: 4, minutes: 32, seconds: 10 });

  // Scroll tracking for Navbar & Sticky CTA
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setSetScrolled(true);
      } else {
        setSetScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          // Reset to 6 hours for high urgency simulation
          return { hours: 5, minutes: 59, seconds: 59 };
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-redirect to secure checkout after details filled
  useEffect(() => {
    let interval;
    if (formSubmitted && redirectTimer > 0) {
      interval = setInterval(() => {
        setRedirectTimer(prev => prev - 1);
      }, 1000);
    } else if (formSubmitted && redirectTimer === 0) {
      window.location.href = CHECKOUT_URL;
    }
    return () => clearInterval(interval);
  }, [formSubmitted, redirectTimer]);

  const formatNumber = (num) => String(num).padStart(2, "0");

  const toggleStruggle = (key) => {
    setStruggles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSelectedStrugglesCount = () => {
    return Object.values(struggles).filter(Boolean).length;
  };

  const toggleFaq = (index) => {
    setFaqExpanded(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const openEnrollModal = () => {
    setIsModalOpen(true);
    setFormSubmitted(false);
    setRedirectTimer(4);
  };

  const closeEnrollModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.whatsapp || !formData.agreeTime) {
      alert("Please fill in all fields and check the commitment box.");
      return;
    }
    setFormSubmitted(true);
  };

  const scrollIntoSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  // Phase Data for curriculum
  const phaseData = {
    1: {
      title: "RESET",
      subtitle: "Days 1–6",
      focus: ["Awareness", "Appreciation", "Body", "Money", "Work"],
      desc: "Lay down the baseline. We shift away from reactive modes, opening up physical and mental storage capacity to appreciate where we are right now."
    },
    2: {
      title: "RECEIVE",
      subtitle: "Days 7–12",
      focus: ["Negativity Detox", "Food Harmony", "Money Blocks", "Subconscious Morning Rituals"],
      desc: "Remove subconscious blocks holding back abundance. We explore morning practices and rewrite negative thought patterns regarding nourishment and wealth."
    },
    3: {
      title: "CREATE",
      subtitle: "Days 13–18",
      focus: ["Core Desires", "Subconscious Vision", "Relationships", "True Health Mapping", "Wealth Attraction", "Empowered Action"],
      desc: "Transition into manifestation mode. Learn to map out exactly what you want across financial, personal, and wellness horizons and seed them in the subconscious."
    },
    4: {
      title: "EXPAND",
      subtitle: "Days 19–24",
      focus: ["Heart Connection", "Breath Mastery", "Universal Contribution", "Somatic Embodiment"],
      desc: "Stretch your boundary. Expand your field of perception and somatic capacity, aligning body and breath to attract larger-scale opportunities."
    },
    5: {
      title: "EMBODY",
      subtitle: "Days 25–30",
      focus: ["Lessons Integration", "Inherent Self-Worth", "Identity Recalibration", "Future Vision Integration"],
      desc: "Stabilize the frequency. Lock in your newly acquired habits, step fully into your self-worth, and align your everyday identity with your highest potential."
    }
  };

  // FAQ List
  const faqList = [
    {
      q: "Do I need to meditate?",
      a: "No. The practices are designed to be beginner-friendly. They involve mindset tools, emotional awareness, journal prompts, and simple everyday actions rather than sitting in hours of silent meditation."
    },
    {
      q: "How much time do I need every day?",
      a: "Approximately 20–25 minutes. This is divided into a short audio teaching, a simple guided practice or somatic reflection, and journal prompts that you can fill at your convenience."
    },
    {
      q: "Do I need to attend live every day?",
      a: "No. All materials are pre-recorded and delivered digitally (such as recordings/WhatsApp content) so you can access them at whatever time fits your schedule perfectly."
    },
    {
      q: "What if I miss a day?",
      a: "Don't quit. This journey is about training consistency, not hitting absolute perfection. If you miss a day, simply resume from where you are without feeling guilty."
    },
    {
      q: "Is this only about manifestation?",
      a: "No. It is a comprehensive blend of NLP (Neuro-Linguistic Programming), modern neuroscience-based mindset tools, appreciation practices, relationship coaching, and practical action."
    },
    {
      q: "Is this a medical or financial program?",
      a: "No. It is a personal development and wellbeing journey. It is for educational purposes only and does not replace medical, psychological, or professional financial advice."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A111F] text-[#F4F1EA] selection:bg-[#E5A93C] selection:text-[#0A111F]">
      
      {/* HEADER / NAVIGATION BAR */}
      <header>
        <nav 
          data-testid="main-navbar"
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl ${
            scrolled ? "bg-[#0A111F]/90 border-b border-[#E5A93C]/20 py-3 shadow-lg" : "bg-transparent py-5"
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => scrollIntoSection("hero")}
              >
                <div className="h-9 w-9 rounded-full bg-[#E5A93C] flex items-center justify-center shadow-md shadow-[#E5A93C]/20">
                  <Sparkles className="h-5 w-5 text-[#0A111F] animate-pulse" />
                </div>
                <div>
                  <span className="font-serif-display text-lg sm:text-xl font-bold tracking-tight text-[#F4F1EA]">
                    Sneha Rath
                  </span>
                  <span className="block text-[10px] uppercase tracking-widest text-[#E5A93C] font-semibold -mt-1">
                    Neurocoach
                  </span>
                </div>
              </div>

              {/* Desktop Nav Links */}
              <div className="hidden md:flex items-center space-x-8 font-sans-body">
                <button 
                  onClick={() => scrollIntoSection("struggles")}
                  className="text-sm text-[#A3B8B0] hover:text-[#E5A93C] transition-colors duration-200"
                >
                  Is This You?
                </button>
                <button 
                  onClick={() => scrollIntoSection("experience")}
                  className="text-sm text-[#A3B8B0] hover:text-[#E5A93C] transition-colors duration-200"
                >
                  Experience
                </button>
                <button 
                  onClick={() => scrollIntoSection("curriculum")}
                  className="text-sm text-[#A3B8B0] hover:text-[#E5A93C] transition-colors duration-200"
                >
                  Curriculum
                </button>
                <button 
                  onClick={() => scrollIntoSection("mentor")}
                  className="text-sm text-[#A3B8B0] hover:text-[#E5A93C] transition-colors duration-200"
                >
                  Coach
                </button>
                <button 
                  onClick={() => scrollIntoSection("faq")}
                  className="text-sm text-[#A3B8B0] hover:text-[#E5A93C] transition-colors duration-200"
                >
                  FAQs
                </button>
              </div>

              {/* Desktop CTA */}
              <div className="hidden md:flex items-center space-x-4">
                <button 
                  data-testid="navbar-cta"
                  onClick={openEnrollModal}
                  className="relative inline-flex items-center justify-center px-5 py-2.5 rounded-full font-semibold text-xs uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-md shadow-[#E5A93C]/10"
                >
                  Enroll Now • ₹999
                </button>
              </div>

              {/* Mobile Hamburger Toggler */}
              <div className="flex md:hidden">
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-[#F4F1EA] hover:text-[#E5A93C] p-1.5 focus:outline-none"
                  aria-label="Toggle Menu"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Nav Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-[#0A111F] border-b border-[#E5A93C]/10 px-4 pt-4 pb-6 space-y-4 shadow-2xl transition-all duration-300">
              <button 
                onClick={() => scrollIntoSection("struggles")}
                className="block w-full text-left text-base font-medium py-2 text-[#A3B8B0] hover:text-[#E5A93C]"
              >
                Is This You?
              </button>
              <button 
                onClick={() => scrollIntoSection("experience")}
                className="block w-full text-left text-base font-medium py-2 text-[#A3B8B0] hover:text-[#E5A93C]"
              >
                Experience
              </button>
              <button 
                onClick={() => scrollIntoSection("curriculum")}
                className="block w-full text-left text-base font-medium py-2 text-[#A3B8B0] hover:text-[#E5A93C]"
              >
                Curriculum
              </button>
              <button 
                onClick={() => scrollIntoSection("mentor")}
                className="block w-full text-left text-base font-medium py-2 text-[#A3B8B0] hover:text-[#E5A93C]"
              >
                Coach
              </button>
              <button 
                onClick={() => scrollIntoSection("faq")}
                className="block w-full text-left text-base font-medium py-2 text-[#A3B8B0] hover:text-[#E5A93C]"
              >
                FAQs
              </button>
              <button 
                onClick={openEnrollModal}
                className="block w-full text-center py-3 rounded-xl bg-[#E5A93C] text-[#0A111F] font-bold text-sm uppercase tracking-wider shadow-lg"
              >
                Enroll Now • ₹999
              </button>
            </div>
          )}
        </nav>
      </header>

      <main>
        {/* HERO SECTION */}
        <section 
          id="hero"
          data-testid="hero-section"
          className="relative pt-32 pb-20 sm:pt-40 sm:pb-32 overflow-hidden flex items-center"
        >
        {/* Background glow effects */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#3B82F6]/5 blur-3xl -z-10" />
        <div className="absolute top-1/3 right-10 w-72 h-72 rounded-full bg-[#E5A93C]/5 blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Hero Text */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8 text-left">
              <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-[#E5A93C]/10 border border-[#E5A93C]/20">
                <span className="h-2 w-2 rounded-full bg-[#E5A93C] animate-ping" />
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#E5A93C]">
                  Limited Offer • Special ₹999 Pricing Active
                </span>
              </div>

              <div className="space-y-4">
                <span className="block text-xs sm:text-sm font-semibold tracking-[0.25em] text-[#E5A93C] uppercase">
                  30 Days of
                </span>
                <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#F4F1EA]">
                  Secrets to Health, <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E5A93C] to-[#E6C99C]">Wealth & Abundance</span>
                </h1>
              </div>

              <p className="font-serif-display text-lg sm:text-xl lg:text-2xl text-[#A3B8B0] italic max-w-xl leading-relaxed">
                "What if the life you're looking for doesn't need more hustle… but a different relationship with what you already have?"
              </p>

              <div className="border-l-2 border-[#E5A93C]/50 pl-4 space-y-3">
                <p className="font-sans-body text-sm sm:text-base text-[#D1CFC7] leading-relaxed max-w-2xl">
                  A 30-day guided transformation journey to help you shift your relationship with health, money, relationships, self-worth and abundance — one simple practice at a time.
                </p>
                <div className="flex items-center space-x-2 text-[#E5A93C] font-semibold text-xs sm:text-sm tracking-wide">
                  <Clock className="h-4 w-4 text-[#E5A93C]" />
                  <span>Just 20–25 minutes a day. Pre-recorded, self-paced, high impact.</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 pt-2">
                <button
                  data-testid="hero-cta"
                  onClick={openEnrollModal}
                  className="px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] hover:shadow-[0_0_20px_rgba(229,169,60,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 text-center"
                >
                  Yes, I Want to Begin My 30-Day Journey
                </button>
                <button
                  onClick={() => scrollIntoSection("curriculum")}
                  className="px-6 py-4 rounded-full font-medium text-sm text-[#F4F1EA] border border-[#E5A93C]/30 hover:border-[#E5A93C] hover:bg-[#E5A93C]/5 transition-all duration-200 text-center"
                >
                  Explore Curriculum
                </button>
              </div>

              <div className="flex items-center space-x-3 pt-2 text-[#A3B8B0] text-xs">
                <div className="flex -space-x-1.5 overflow-hidden">
                  <img className="inline-block h-6 w-6 rounded-full ring-2 ring-[#0A111F]" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=60&h=60" alt="Attendee 1" />
                  <img className="inline-block h-6 w-6 rounded-full ring-2 ring-[#0A111F]" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=60&h=60" alt="Attendee 2" />
                  <img className="inline-block h-6 w-6 rounded-full ring-2 ring-[#0A111F]" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=60&h=60" alt="Attendee 3" />
                </div>
                <span>Join <strong>1,420+ participants</strong> who shifted their subconscious blocks.</span>
              </div>
            </div>

            {/* Hero Interactive Image Card */}
            <div className="lg:col-span-5 relative mt-6 lg:mt-0">
              <div className="relative mx-auto max-w-[360px] lg:max-w-none">
                {/* Visual Accent Borders */}
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-tr from-[#E5A93C]/20 to-[#3B82F6]/20 blur-xl -z-10" />
                <div className="absolute -top-4 -left-4 w-12 h-12 border-t-2 border-l-2 border-[#E5A93C] rounded-tl-xl" />
                <div className="absolute -bottom-4 -right-4 w-12 h-12 border-b-2 border-r-2 border-[#E5A93C] rounded-br-xl" />

                {/* Main Hero Card */}
                <div className="overflow-hidden rounded-2xl border border-[#E5A93C]/30 bg-[#132238] p-4 sm:p-5 shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9uJTIwd29tYW58ZW58MHx8fHwxNzkxMjU3OTM3fDA&ixlib=rb-4.1.0&q=85"
                    alt="Serene self-reflection and meditation illustration" 
                    className="w-full h-48 sm:h-56 object-cover rounded-xl filter brightness-95 hover:scale-[1.01] transition-transform duration-300"
                  />
                  
                  <div className="mt-5 space-y-4 text-left">
                    <div className="flex justify-between items-center border-b border-[#E5A93C]/10 pb-3">
                      <div>
                        <h4 className="font-serif-display text-base font-semibold text-[#F4F1EA]">
                          Complete 30-Day Guide
                        </h4>
                        <p className="text-xs text-[#A3B8B0]">Guided Subconscious Reprogramming</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-[#E5A93C] font-semibold uppercase tracking-wider block">Special Price</span>
                        <span className="text-lg font-bold text-[#E5A93C]">₹999/-</span>
                      </div>
                    </div>

                    <ul className="space-y-2 text-xs text-[#D1CFC7]">
                      <li className="flex items-center space-x-2">
                        <Check className="h-3.5 w-3.5 text-[#3B82F6] flex-shrink-0" />
                        <span>30 High-Quality Audio Lessons & Somatic Practices</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-3.5 w-3.5 text-[#3B82F6] flex-shrink-0" />
                        <span>Curated Journal Prompts & Action Workbooks</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <Check className="h-3.5 w-3.5 text-[#3B82F6] flex-shrink-0" />
                        <span>Daily WhatsApp Reminders & Community support</span>
                      </li>
                    </ul>

                    {/* Simulating active counter */}
                    <div className="bg-[#0A111F]/80 rounded-xl p-3 border border-[#E5A93C]/15 text-center">
                      <p className="text-[10px] text-[#A3B8B0] uppercase tracking-widest font-semibold mb-1">
                        ✨ SPECIAL ENROLLMENT WINDOW ENDS IN
                      </p>
                      <div data-testid="countdown-timer" className="font-mono text-base font-bold text-[#E5A93C] tracking-widest flex justify-center space-x-2">
                        <span>{formatNumber(timeLeft.hours)}h</span>
                        <span className="animate-pulse">:</span>
                        <span>{formatNumber(timeLeft.minutes)}m</span>
                        <span className="animate-pulse">:</span>
                        <span>{formatNumber(timeLeft.seconds)}s</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* URGENCY TICKER */}
      <div className="bg-[#E5A93C] text-[#0A111F] py-3 overflow-hidden whitespace-nowrap font-sans-body text-xs sm:text-sm font-semibold tracking-wider border-y border-[#E5A93C]/40">
        <div className="inline-block animate-marquee">
          <span>✨ 30 DAYS OF SECRETS • ENROLL AT ₹999 (REGULAR PRICE ₹4,999) • SHIFT YOUR HEALTH, WEALTH & RELATIONSHIPS • SNEHA RATH NEUROCOACH • </span>
          <span>✨ 30 DAYS OF SECRETS • ENROLL AT ₹999 (REGULAR PRICE ₹4,999) • SHIFT YOUR HEALTH, WEALTH & RELATIONSHIPS • SNEHA RATH NEUROCOACH • </span>
        </div>
      </div>

      {/* SECTION 2 — DOES THIS SOUND LIKE YOU? */}
      <section 
        id="struggles"
        data-testid="struggles-section"
        className="py-16 sm:py-24 bg-[#132238]/40 border-b border-[#E5A93C]/10"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 mb-10">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              Self-Reflection Check
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              You are doing a lot. <br />
              <span className="text-[#A3B8B0]">But somewhere inside, do you still feel:</span>
            </h2>
            <p className="font-serif-display text-lg sm:text-xl text-[#E5A93C] italic">
              "Something is missing."
            </p>
            <p className="text-xs text-[#A3B8B0] font-sans-body max-w-lg mx-auto">
              (Interactive checklist: Tap or check the ones that resonate with you below to unlock Sneha's guidance.)
            </p>
          </div>

          {/* Checklist Area */}
          <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/15 p-6 sm:p-8 space-y-4 shadow-xl">
            
            {/* Struggle item 1 */}
            <div 
              onClick={() => toggleStruggle("financial")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.financial 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.financial}
                onChange={() => {}} // Handled by outer div click
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-0"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You work hard but still feel financially restricted.
              </span>
            </div>

            {/* Struggle item 2 */}
            <div 
              onClick={() => toggleStruggle("consistency")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.consistency 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.consistency}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-1"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You want to take care of your health but struggle with consistency.
              </span>
            </div>

            {/* Struggle item 3 */}
            <div 
              onClick={() => toggleStruggle("overthinking")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.overthinking 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.overthinking}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-2"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                Your mind is constantly thinking about the next thing.
              </span>
            </div>

            {/* Struggle item 4 */}
            <div 
              onClick={() => toggleStruggle("wantMore")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.wantMore 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.wantMore}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-3"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You feel grateful for your life… but still want MORE.
              </span>
            </div>

            {/* Struggle item 5 */}
            <div 
              onClick={() => toggleStruggle("noDirection")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.noDirection 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.noDirection}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-4"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You have dreams but don't know where to begin.
              </span>
            </div>

            {/* Struggle item 6 */}
            <div 
              onClick={() => toggleStruggle("forgetSelf")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.forgetSelf 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.forgetSelf}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-5"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You give so much to everyone else that you forget yourself.
              </span>
            </div>

            {/* Struggle item 7 */}
            <div 
              onClick={() => toggleStruggle("repeatingPatterns")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.repeatingPatterns 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.repeatingPatterns}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-6"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You sometimes wonder why certain patterns keep repeating.
              </span>
            </div>

            {/* Struggle item 8 */}
            <div 
              onClick={() => toggleStruggle("peaceCost")}
              className={`flex items-start space-x-3 p-3.5 rounded-xl border cursor-pointer transition-all duration-200 ${
                struggles.peaceCost 
                  ? "bg-[#E5A93C]/10 border-[#E5A93C]" 
                  : "bg-[#0A111F]/40 border-transparent hover:border-[#E5A93C]/20"
              }`}
            >
              <input 
                type="checkbox" 
                checked={struggles.peaceCost}
                onChange={() => {}}
                className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-1 flex-shrink-0"
                data-testid="struggle-checkbox-7"
              />
              <span className="text-sm sm:text-base text-[#D1CFC7] select-none">
                You want abundance — but not at the cost of your peace.
              </span>
            </div>

          </div>

          {/* Personalization Result Banner */}
          {getSelectedStrugglesCount() > 0 && (
            <div 
              data-testid="struggles-personalization-banner"
              className="mt-6 p-5 rounded-2xl bg-gradient-to-r from-[#132238] to-[#050B14] border-l-4 border-[#E5A93C] shadow-lg animate-fade-in"
            >
              <p className="text-sm text-[#F4F1EA] font-sans-body">
                ✨ You checked <strong>{getSelectedStrugglesCount()} out of 8</strong> loops. This is incredibly common. Subconscious programming drives 95% of our actions. By shifting what you unconsciously prioritize, you can release these obstacles. <strong>This 30-day program was built to reset these exact blocks.</strong>
              </p>
            </div>
          )}

          <div className="mt-8 text-center space-y-4">
            <p className="font-serif-display text-lg text-[#F4F1EA]">
              If you nodded to even one of these… <br />
              <strong className="text-[#E5A93C] text-xl not-italic">This journey was created for you.</strong>
            </p>
            <button 
              onClick={openEnrollModal}
              className="inline-flex items-center space-x-2 text-[#E5A93C] hover:text-[#F5B84C] font-semibold text-sm group transition-colors"
            >
              <span>Begin breaking these loops now</span>
              <ArrowRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT IF? */}
      <section 
        id="what-if"
        data-testid="what-if-section"
        className="py-16 sm:py-24 bg-[#0A111F] overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              A Shift in Perspective
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              What if abundance isn't just about having more money?
            </h2>
            <div className="w-16 h-1 bg-[#E5A93C]/50 mx-auto rounded-full mt-4" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-4 text-center">
            {[
              { text: "More energy", icon: "✨" },
              { text: "More self-trust", icon: "✨" },
              { text: "More relationships", icon: "✨" },
              { text: "More clarity", icon: "✨" },
              { text: "More opportunities", icon: "✨" },
              { text: "More peace", icon: "✨" },
              { text: "More joy", icon: "✨" },
              { text: "More confidence", icon: "✨" },
              { text: "More possibility", icon: "✨" }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="lg:col-span-1 p-5 rounded-2xl bg-[#132238] border border-[#E5A93C]/10 hover:border-[#E5A93C]/30 hover:scale-[1.03] transition-all duration-300 flex flex-col items-center justify-center space-y-2 group"
              >
                <span className="text-2xl group-hover:animate-bounce">{item.icon}</span>
                <span className="text-xs sm:text-sm font-semibold tracking-wide text-[#F4F1EA]">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center max-w-2xl mx-auto space-y-6">
            <p className="font-serif-display text-lg sm:text-xl text-[#A3B8B0] leading-relaxed italic">
              "30 Days of Secrets to Health, Wealth & Abundance is designed to help you begin seeing your life through a different lens."
            </p>
            <button 
              onClick={() => scrollIntoSection("experience")}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full font-medium text-xs uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-colors"
            >
              See What You Will Experience
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT YOU WILL EXPERIENCE */}
      <section 
        id="experience"
        data-testid="experience-section"
        className="py-16 sm:py-24 bg-[#132238]/40 border-y border-[#E5A93C]/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16 max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              Transformation Pillars
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              What You Will Experience
            </h2>
            <p className="text-sm text-[#A3B8B0] max-w-md mx-auto">
              A holistic neuro-coaching framework targeting the core areas of your subconscious and energetic patterns.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            
            {/* Health */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-[#3B82F6]/5 rounded-bl-full group-hover:bg-[#3B82F6]/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center border border-[#3B82F6]/20 mb-6">
                <LeafIcon className="h-6 w-6 text-[#3B82F6]" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                🌿 Health
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Build a deeper appreciation for your body, energy and everyday wellbeing. Move from feeling drained into deep physical and emotional vitality.
              </p>
            </div>

            {/* Wealth */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-[#E5A93C]/5 rounded-bl-full group-hover:bg-[#E5A93C]/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-[#E5A93C]/10 flex items-center justify-center border border-[#E5A93C]/20 mb-6">
                <Coins className="h-6 w-6 text-[#E5A93C]" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                💰 Wealth
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Explore your relationship with money, receiving, value and financial possibility. Reset negative money blocks and open to prosperous possibilities.
              </p>
            </div>

            {/* Relationships */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-red-500/5 rounded-bl-full group-hover:bg-red-500/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20 mb-6">
                <Heart className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                ❤️ Relationships
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Shift from criticism and resentment toward appreciation, connection and conscious relationships. Build deeper bonds with loved ones.
              </p>
            </div>

            {/* Mindset */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-blue-500/5 rounded-bl-full group-hover:bg-blue-500/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 mb-6">
                <Brain className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                🧠 Mindset
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Become aware of the thoughts and attention patterns shaping your everyday experience. Rewire self-limiting stories using proven NLP structures.
              </p>
            </div>

            {/* Abundance */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-purple-500/5 rounded-bl-full group-hover:bg-purple-500/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20 mb-6">
                <Sparkles className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                ✨ Abundance
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Learn to notice opportunities, possibilities and resources that you may have been overlooking. Open up to the flow of unconditional universe.
              </p>
            </div>

            {/* Self-Worth */}
            <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/10 p-6 sm:p-8 hover:border-[#E5A93C]/30 transition-all duration-300 relative group overflow-hidden">
              <div className="absolute top-0 right-0 h-24 w-24 bg-pink-500/5 rounded-bl-full group-hover:bg-pink-500/10 transition-colors" />
              <div className="h-12 w-12 rounded-xl bg-pink-500/10 flex items-center justify-center border border-pink-500/20 mb-6">
                <Flower2 className="h-6 w-6 text-pink-400" />
              </div>
              <h3 className="font-serif-display text-lg sm:text-xl font-bold text-[#F4F1EA] mb-3 uppercase tracking-wide">
                🌸 Self-Worth
              </h3>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                Move from constantly asking "Am I enough?" toward recognizing your inherent value. Establish unbreakable alignment with your higher self.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 5 — YOUR 30-DAY JOURNEY (Curriculum Switcher) */}
      <section 
        id="curriculum"
        data-testid="curriculum-grid"
        className="py-16 sm:py-24 bg-[#0A111F]"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 mb-12">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              Step-by-Step Pathway
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Your 30-Day Journey
            </h2>
            <p className="text-sm text-[#A3B8B0] max-w-md mx-auto">
              Click the stages below to explore how you will gradually recalibrate your nervous system and consciousness over the 30 days.
            </p>
          </div>

          {/* Tab buttons */}
          <div className="flex flex-nowrap overflow-x-auto pb-4 scrollbar-hide gap-2 justify-start sm:justify-center border-b border-[#E5A93C]/10 mb-8">
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                onClick={() => setActiveTab(num)}
                data-testid={`curriculum-tab-${num}`}
                className={`px-5 py-3 rounded-t-xl font-semibold text-xs uppercase tracking-wider flex-shrink-0 transition-all ${
                  activeTab === num 
                    ? "bg-[#132238] text-[#E5A93C] border-t-2 border-x border-[#E5A93C]/40" 
                    : "text-[#A3B8B0] hover:text-[#E5A93C]"
                }`}
              >
                Phase {num}: {phaseData[num].title}
                <span className="block text-[10px] text-gray-500 normal-case font-normal mt-0.5">
                  {phaseData[num].subtitle}
                </span>
              </button>
            ))}
          </div>

          {/* Curriculum Phase Content */}
          <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/20 p-6 sm:p-10 shadow-2xl relative overflow-hidden min-h-[250px] flex flex-col justify-between">
            <div className="absolute top-4 right-6 text-7xl font-bold font-serif-display text-[#E5A93C]/5 select-none pointer-events-none">
              DAYS {phaseData[activeTab].subtitle.replace("Days ", "")}
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E5A93C]">
                  Phase {activeTab}
                </span>
                <span className="h-1 w-1 rounded-full bg-[#E5A93C]" />
                <span className="text-xs font-semibold text-[#A3B8B0]">
                  {phaseData[activeTab].subtitle}
                </span>
              </div>

              <h3 className="font-serif-display text-2xl sm:text-3xl font-extrabold text-[#F4F1EA] tracking-wide">
                {phaseData[activeTab].title}
              </h3>

              <p className="text-sm sm:text-base text-[#D1CFC7] leading-relaxed max-w-2xl">
                {phaseData[activeTab].desc}
              </p>

              <div>
                <span className="block text-xs uppercase tracking-widest font-bold text-[#E5A93C] mb-3">
                  CORE MODULES COVERED:
                </span>
                <div className="flex flex-wrap gap-2.5">
                  {phaseData[activeTab].focus.map((item, idx) => (
                    <span 
                      key={idx}
                      className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-[#0A111F] border border-[#E5A93C]/10 text-[#F4F1EA] flex items-center space-x-1.5"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" />
                      <span>{item}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-[#E5A93C]/10 mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-xs text-[#A3B8B0]">
                Requires only 20–25 minutes of self-guided study per day.
              </div>
              <button 
                onClick={openEnrollModal}
                className="px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-all"
              >
                Begin Journey for ₹999
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — YOU DON'T NEED HOURS & SECTION 7 — WHAT YOU'LL NEED */}
      <section 
        id="requirements"
        data-testid="requirements-section"
        className="py-16 sm:py-24 bg-[#132238]/40 border-y border-[#E5A93C]/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            
            {/* Left Card: You don't need hours */}
            <div className="lg:col-span-7 bg-[#132238] rounded-2xl border border-[#E5A93C]/15 p-6 sm:p-10 shadow-xl flex flex-col justify-between">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E5A93C]">
                  Effortless Integration
                </span>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-bold mt-2 mb-4">
                  You Don't Need Hours
                </h3>
                <p className="text-sm sm:text-base text-[#A3B8B0] mb-8 leading-relaxed">
                  We don't expect you to wake up at 4 AM, sit in hour-long silences, or isolate yourself. Just <strong>20–25 minutes a day</strong> of conscious work is all it takes to trigger transformation.
                </p>

                <div className="space-y-4">
                  <span className="block text-xs uppercase tracking-wider font-bold text-[#E5A93C]">
                    WHAT YOU WILL RECEIVE EVERY DAY:
                  </span>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: "🎧", title: "A short teaching", desc: "Mindset concepts and NLP tools explained clearly." },
                      { icon: "🧘", title: "A guided practice", desc: "Short breathwork, visualization, or somatic release." },
                      { icon: "📓", title: "Journal prompts", desc: "Deep self-inquiry queries to uncover blocks." },
                      { icon: "🌱", title: "One simple action", desc: "Approachable practices to wire learnings into action." },
                      { icon: "🌙", title: "Daily Integration", desc: "Settle and ground your somatic frequency." }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start space-x-3 bg-[#0A111F]/40 p-3 rounded-xl border border-transparent hover:border-[#E5A93C]/10 transition-colors">
                        <span className="text-2xl">{item.icon}</span>
                        <div>
                          <h4 className="text-sm font-bold text-[#F4F1EA]">{item.title}</h4>
                          <p className="text-xs text-[#A3B8B0]">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs text-[#A3B8B0] italic mt-8 border-t border-[#E5A93C]/10 pt-4">
                No complex spiritual credentials needed. Simple, scientifically grounded, and life-friendly.
              </p>
            </div>

            {/* Right Card: What you'll need */}
            <div className="lg:col-span-5 bg-[#0A111F] rounded-2xl border-2 border-[#E5A93C]/30 p-6 sm:p-10 shadow-2xl flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 h-16 w-16 bg-[#E5A93C]/5 rounded-bl-full pointer-events-none" />
              
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E5A93C]">
                  Your Preparation
                </span>
                <h3 className="font-serif-display text-2xl sm:text-3xl font-bold mt-2 mb-4">
                  What You'll Need
                </h3>
                <p className="text-sm text-[#A3B8B0] mb-8">
                  The requirements are deliberately minimal to remove friction. The real magic depends on your willingness.
                </p>

                <div className="space-y-4">
                  {[
                    "A physical journal to map your reflections",
                    "A pen that feels comfortable to write with",
                    "20–25 quiet, dedicated minutes a day",
                    "An open mind, willing to challenge comfort loops",
                    "Deep willingness to show up and participate"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-sm sm:text-base text-[#D1CFC7]">
                      <div className="h-5 w-5 rounded-full bg-[#E5A93C]/10 border border-[#E5A93C]/30 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-[#E5A93C]" />
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 bg-[#132238] rounded-xl p-5 border border-[#E5A93C]/20 text-center">
                <span className="block text-xs uppercase tracking-widest font-bold text-[#E5A93C] mb-1">
                  MOST IMPORTANTLY:
                </span>
                <p className="font-serif-display text-base sm:text-lg text-[#F4F1EA] italic">
                  "Your commitment to yourself."
                </p>
                <button 
                  onClick={openEnrollModal}
                  className="mt-4 w-full py-3 rounded-full font-bold text-xs uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-all"
                >
                  Make The Commitment • ₹999
                </button>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* SECTION 8 — THIS IS NOT ABOUT... */}
      <section 
        id="not-about"
        data-testid="real-talk-section"
        className="py-16 sm:py-24 bg-[#0A111F]"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex h-12 w-12 rounded-full bg-red-500/10 border border-red-500/30 items-center justify-center mb-6">
            <AlertTriangle className="h-6 w-6 text-red-400" />
          </div>

          <span className="block text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C] mb-3">
            Real Talk & Subconscious Integrity
          </span>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">
            This Is NOT About...
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-10">
            <div className="p-5 rounded-xl bg-[#132238]/40 border border-red-500/10 flex items-start space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-[#F4F1EA]">Pretending everything is perfect</h4>
                <p className="text-xs text-[#A3B8B0] mt-1">We don't ignore facts. We look at blocks with complete scientific honesty.</p>
              </div>
            </div>

            <div className="p-5 rounded-xl bg-[#132238]/40 border border-red-500/10 flex items-start space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-[#F4F1EA]">Ignoring difficult emotions</h4>
                <p className="text-xs text-[#A3B8B0] mt-1">Anger, fear, and fatigue are signals. We integrate them somatically rather than suppressing.</p>
              </div>
            </div>

            <div className="p-5 rounded-xl bg-[#132238]/40 border border-red-500/10 flex items-start space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-[#F4F1EA]">Toxic positivity</h4>
                <p className="text-xs text-[#A3B8B0] mt-1">Forcing happy thoughts over deep-seated traumatic stress doesn't work. We clear subconscious root structures.</p>
              </div>
            </div>

            <div className="p-5 rounded-xl bg-[#132238]/40 border border-red-500/10 flex items-start space-x-3">
              <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-[#F4F1EA]">Magical sitting and waiting</h4>
                <p className="text-xs text-[#A3B8B0] mt-1">Manifestation requires clean, integrated, conscious actions. We pair neural retraining with daily actions.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/20 p-6 sm:p-8 space-y-4">
            <span className="text-xs uppercase tracking-widest font-bold text-[#E5A93C] block">
              INSTEAD, THIS JOURNEY IS ABOUT:
            </span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { title: "Changing what you notice", desc: "Perception training" },
                { title: "Changing what you practice", desc: "Nervous retraining" },
                { title: "Changing how you respond", desc: "Ego-response integration" },
                { title: "Conscious creation", desc: "Aligned energetic action" }
              ].map((item, idx) => (
                <div key={idx} className="p-4 bg-[#0A111F]/80 rounded-xl border border-[#E5A93C]/10 flex flex-col justify-between min-h-[110px]">
                  <h4 className="text-sm font-semibold text-[#F4F1EA]">{item.title}</h4>
                  <p className="text-[10px] text-[#E5A93C] uppercase tracking-wider font-semibold mt-2">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* SECTION 9 — IMAGINE YOURSELF 30 DAYS FROM NOW... */}
      <section 
        id="imagine-future"
        data-testid="future-self-section"
        className="relative py-20 sm:py-32 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1536623975707-c4b3b2af565d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzNTl8MHwxfHNlYXJjaHwyfHxtYWRpdGF0aW9uJTIwemVuJTIwc3RvbmVzfGVufDB8fHx8MTc5MTI1NzkzN3ww&ixlib=rb-4.1.0&q=85"
            alt="Mindfulness zen stone structures under golden ambient light" 
            className="w-full h-full object-cover filter opacity-[0.13] brightness-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A111F] via-[#0A111F]/80 to-[#0A111F]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-8">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
            Your New Reality
          </span>
          
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Imagine Yourself 30 Days From Now...
          </h2>

          <div className="max-w-2xl mx-auto space-y-6 font-sans-body text-[#D1CFC7] text-sm sm:text-base leading-relaxed text-left border-l-4 border-[#E5A93C] pl-6 py-2">
            <p>
              You wake up and don't immediately think about everything that's wrong. You notice what is working.
            </p>
            <p>
              You appreciate your body differently, feeling an intrinsic sense of vitality and calm. You look at money differently, discarding feelings of panic and restriction for steady, composed flow.
            </p>
            <p>
              You notice opportunities you previously ignored, become deeply intentional with your relationships, and start trusting your intuition again.
            </p>
            <p className="font-serif-display text-[#E5A93C] text-lg sm:text-xl italic font-semibold">
              "And perhaps most importantly... You begin to feel that life is working WITH you — not constantly happening TO you."
            </p>
          </div>

          <div className="pt-6">
            <button
              onClick={openEnrollModal}
              className="px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg"
            >
              Step Into Your Aligned Self • ₹999
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 10 — WHO IS THIS FOR? */}
      <section 
        id="who-for"
        data-testid="who-is-it-for-section"
        className="py-16 sm:py-24 bg-[#132238]/40 border-y border-[#E5A93C]/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E5A93C]">
                Ideal Alignment
              </span>
              <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                Who Is This For?
              </h2>
              <p className="text-sm sm:text-base text-[#A3B8B0] leading-relaxed">
                This guided program is carefully tuned. You don't need any complex background or years of spiritual exploration. You simply need to be curious enough to explore.
              </p>
              <p className="text-sm sm:text-base text-[#D1CFC7] font-semibold">
                This journey is especially for you if you are:
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {[
                  "A working professional managing burnout",
                  "An entrepreneur growing their scale",
                  "A homemaker carrying multiple burdens",
                  "A woman juggling demanding domestic roles",
                  "Someone feeling emotionally stuck or fatigued",
                  "Someone ready for a calm money relationship",
                  "Someone interested in manifestation mechanics",
                  "Someone ready to build a highly intentional life"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-xs sm:text-sm text-[#D1CFC7]">
                    <span className="text-[#E5A93C]">✨</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-[#132238] rounded-2xl border border-[#E5A93C]/15 p-6 sm:p-8 space-y-6 text-center">
                <div className="h-14 w-14 rounded-full bg-[#E5A93C]/10 border border-[#E5A93C]/30 flex items-center justify-center mx-auto mb-4">
                  <Compass className="h-7 w-7 text-[#E5A93C]" />
                </div>
                
                <h3 className="font-serif-display text-xl font-bold">
                  No "spiritual background" required.
                </h3>
                
                <p className="text-sm text-[#A3B8B0] leading-relaxed">
                  "You don't need to be 'spiritual enough.' <br />
                  You simply need to be curious enough to explore."
                </p>

                <div className="border-t border-[#E5A93C]/10 pt-6">
                  <p className="text-xs text-[#D1CFC7] mb-4">
                    Daily bite-sized audio transmissions (approx 20 mins) delivered on WhatsApp for friction-free consumption.
                  </p>
                  <button
                    onClick={openEnrollModal}
                    className="w-full py-3.5 rounded-full font-bold text-xs uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-all"
                  >
                    Yes, I Fit This Profile • Begin Now
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 11 — MEET YOUR COACH */}
      <section 
        id="mentor"
        data-testid="mentor-section"
        className="py-16 sm:py-24 bg-[#0A111F]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Coach Portrait */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-[320px] lg:max-w-none">
                {/* Visual Frame Accent */}
                <div className="absolute -inset-3 bg-gradient-to-tr from-[#E5A93C]/10 to-transparent rounded-2xl -z-10" />
                <div className="absolute -top-4 -right-4 w-12 h-12 border-t-2 border-r-2 border-[#E5A93C] rounded-tr-xl" />
                <div className="absolute -bottom-4 -left-4 w-12 h-12 border-b-2 border-l-2 border-[#E5A93C] rounded-bl-xl" />

                <div className="overflow-hidden rounded-2xl border border-[#E5A93C]/20 shadow-2xl bg-[#132238]">
                  <img 
                    src="https://tagmango.com/assets/pomelli-image-1-e98376bfbac913ce15c3f51dfe18827d.png"
                    alt="Sneha Rath - Life and NLP Neurocoach Portrait" 
                    className="w-full h-auto max-h-[520px] object-contain bg-[#0A111F] filter contrast-105 hover:scale-[1.01] transition-transform duration-300"
                  />
                  <div className="p-4 bg-[#132238] border-t border-[#E5A93C]/10 text-center">
                    <span className="font-serif-display font-bold text-lg text-[#F4F1EA]">Sneha Rath</span>
                    <p className="text-[10px] uppercase tracking-widest text-[#E5A93C] font-semibold mt-0.5">
                      NLP MASTER NEUROCOACH & SPIRITUAL WELLNESS MENTOR
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coach Biography */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
                MEET YOUR MENTOR
              </span>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#F4F1EA]">
                Sneha Rath
              </h2>
              <p className="font-sans-body text-xs sm:text-sm font-semibold tracking-wide text-[#E5A93C] uppercase">
                Neurocoach | Life Coach | NLP Coach | Spiritual & Emotional Wellness Mentor
              </p>

              <div className="space-y-4 font-sans-body text-[#D1CFC7] text-sm sm:text-base leading-relaxed">
                <p>
                  With a solid background in corporate leadership and life-alignment coaching, Sneha Rath brings together mindset work, subconscious exploration, emotional awareness, gratitude practices, and spiritual tools to help people reconnect with themselves and create meaningful transformation.
                </p>
                <p>
                  Over the past years, she has empowered thousands of professionals, homemakers, and business leaders to decode their self-sabotage loops and build clear pathways to effortless health, balanced prosperity, and absolute alignment.
                </p>
              </div>

              <div className="bg-[#132238] rounded-xl p-5 border border-[#E5A93C]/15 space-y-3">
                <span className="block text-xs uppercase tracking-widest font-bold text-[#E5A93C]">
                  HER INTENTION FOR YOU:
                </span>
                <p className="font-serif-display text-base sm:text-lg text-[#F4F1EA] italic leading-relaxed">
                  "To awaken the inner spark within you — so you don't simply chase a better life, but learn to create one from within."
                </p>
              </div>

              <div className="flex items-center space-x-6 pt-2">
                <div className="flex items-center space-x-2 text-xs text-[#A3B8B0]">
                  <Award className="h-4 w-4 text-[#E5A93C]" />
                  <span>Certified NLP Practitioner</span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-[#A3B8B0]">
                  <CheckCircle2 className="h-4 w-4 text-[#E5A93C]" />
                  <span>10,000+ Lives Impacted</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 12 — THE INVESTMENT (Pricing Block) */}
      <section 
        id="pricing"
        data-testid="pricing-card"
        className="py-16 sm:py-24 bg-[#132238]/40 border-y border-[#E5A93C]/10"
      >
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 mb-12">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              Your Dedicated Commitment
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              The Investment
            </h2>
            <p className="text-sm text-[#A3B8B0]">
              Unlock the complete 30-day curriculum, workbook logs, and direct daily support on WhatsApp.
            </p>
          </div>

          {/* Pricing Box */}
          <div className="relative rounded-2xl border-2 border-[#E5A93C] bg-gradient-to-b from-[#132238] to-[#050B14] p-6 sm:p-10 text-center shadow-[0_0_50px_rgba(229,169,60,0.15)] overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#E5A93C] text-[#0A111F] text-[10px] font-bold uppercase tracking-widest py-1.5 px-6 rounded-bl-xl">
              Limited Special Offer
            </div>

            <span className="text-xs uppercase tracking-[0.2em] text-[#A3B8B0] font-semibold block mt-2">
              COMPLETE 30-DAY PROGRAM
            </span>
            
            <div className="my-6">
              <span className="text-sm text-[#A3B8B0] line-through block mb-1">Regular Value ₹4,999/-</span>
              <span className="text-5xl font-extrabold text-[#E5A93C] tracking-tight">₹999/-</span>
              <span className="text-xs text-[#A3B8B0] block mt-1">One-time payment. Lifetime access.</span>
            </div>

            {/* Price breakdown */}
            <div className="bg-[#0A111F]/80 rounded-xl p-4 border border-[#E5A93C]/10 mb-8 max-w-xs mx-auto text-center">
              <span className="text-xs text-[#A3B8B0]">That's approximately:</span>
              <span className="block font-serif-display text-lg font-bold text-[#E5A93C] my-0.5">₹33 / day</span>
              <span className="text-[10px] text-gray-500">For 30 days of intentional, guided self-work.</span>
            </div>

            <div className="space-y-4 mb-8 text-left text-xs sm:text-sm text-[#D1CFC7] max-w-sm mx-auto">
              <p className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-[#3B82F6] flex-shrink-0" />
                <span>You are not buying information.</span>
              </p>
              <p className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-[#3B82F6] flex-shrink-0" />
                <span>You are giving yourself 30 days of dedicated attention.</span>
              </p>
            </div>

            <button
              data-testid="pricing-cta"
              onClick={openEnrollModal}
              className="w-full py-4 rounded-full font-bold text-sm uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] hover:shadow-[0_0_20px_rgba(229,169,60,0.35)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              Claim Your Journey For ₹999
            </button>

            <div className="mt-4 flex items-center justify-center space-x-1.5 text-[10px] text-[#A3B8B0]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3B82F6]" />
              <span>Secure Checkout • 100% Satisfaction Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 13 — FAQ (Interactive Accordions) */}
      <section 
        id="faq"
        data-testid="faq-section"
        className="py-16 sm:py-24 bg-[#0A111F]"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 mb-16">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E5A93C]">
              Have Questions?
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Frequently Asked Questions
            </h2>
            <div className="w-12 h-0.5 bg-[#E5A93C]/50 mx-auto rounded-full mt-4" />
          </div>

          <div className="space-y-4">
            {faqList.map((faq, index) => {
              const isExpanded = !!faqExpanded[index];
              return (
                <div 
                  key={index}
                  data-testid={`faq-item-${index}`}
                  className="bg-[#132238] rounded-xl border border-[#E5A93C]/10 overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-[#132238]/80 transition-colors"
                  >
                    <span className="font-serif-display font-bold text-base sm:text-lg text-[#F4F1EA] pr-4">
                      {faq.q}
                    </span>
                    <span className="text-[#E5A93C] flex-shrink-0">
                      {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="px-6 pb-6 text-sm text-[#A3B8B0] leading-relaxed border-t border-[#E5A93C]/5 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10 p-4 rounded-xl bg-[#132238]/30 border border-[#E5A93C]/10 text-center text-xs text-gray-500 italic">
            Disclaimer: This personal-development and subconscious reprogramming journey is designed for conscious coaching, alignment and wellbeing. It does not constitute medical, psychological, or professional financial advice.
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section 
        id="final-cta"
        data-testid="final-cta-section"
        className="relative py-20 sm:py-32 bg-[#132238]/40 border-t border-[#E5A93C]/20 overflow-hidden text-center"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-[#E5A93C]/5 blur-3xl pointer-events-none" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6 sm:space-y-8 relative z-10">
          <p className="font-serif-display text-lg sm:text-xl lg:text-2xl text-[#D1CFC7] leading-relaxed italic max-w-2xl mx-auto">
            "Your life doesn't need to become perfect for you to feel abundant. You can begin by changing the way you notice, appreciate, think, feel and act."
          </p>

          <div className="w-16 h-0.5 bg-[#E5A93C]/50 mx-auto rounded-full" />

          <div className="space-y-3">
            <span className="block text-[10px] sm:text-xs font-bold tracking-[0.3em] text-[#E5A93C] uppercase">
              Begin Today • Just 20 Mins A Day
            </span>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              30 Days of Secrets to Health, Wealth & Abundance
            </h2>
            <p className="text-lg font-bold text-[#E5A93C]">₹999/- only</p>
          </div>

          <div className="pt-2">
            <button
              data-testid="final-cta-btn"
              onClick={openEnrollModal}
              className="px-10 py-5 rounded-full font-bold text-sm uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] hover:shadow-[0_0_25px_rgba(229,169,60,0.4)] hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Yes, I'm In
            </button>
          </div>

          <p className="font-serif-display text-base sm:text-lg text-[#F4F1EA] italic tracking-wide">
            "Your healthier. Your wealthier. Your more abundant self is waiting. ✨"
          </p>
        </div>
      </section>

      </main>

      {/* FOOTER */}
      <footer className="bg-[#0A111F] border-t border-[#E5A93C]/10 py-12 text-center text-xs text-[#A3B8B0] font-sans-body">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          
          <div className="flex justify-center items-center space-x-2">
            <div className="h-6 w-6 rounded-full bg-[#E5A93C] flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-[#0A111F]" />
            </div>
            <span className="font-serif-display font-bold text-[#F4F1EA]">Sneha Rath • Neurocoach</span>
          </div>

          <p className="max-w-2xl mx-auto text-gray-500 leading-relaxed px-4">
            Disclaimer: "30 Days of Secrets to Health, Wealth & Abundance" is a structured personal development and coaching program led by Sneha Rath. It utilizes NLP, subconscious mindset rewiring, and emotional alignment techniques. This is NOT a financial, investment, legal, psychological or medical program. Please consult qualified professionals for medical or financial decisions.
          </p>

          <div className="border-t border-[#E5A93C]/5 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-[10px] text-gray-600">
            <p>© {new Date().getFullYear()} Sneha Rath Coaching. All rights reserved.</p>
            <p>
              Branded Domain: <a href="https://neurocoach.sneharath.com" className="text-[#E5A93C] hover:underline">neurocoach.sneharath.com</a>
            </p>
            <p>Powered by TagMango Mango AI Funnel Builder</p>
          </div>

        </div>
      </footer>

      {/* STICKY MOBILE CTA BAR */}
      <div 
        data-testid="sticky-mobile-cta"
        className={`md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#132238]/95 border-t border-[#E5A93C]/30 px-4 py-3 shadow-[0_-5px_15px_rgba(0,0,0,0.4)] flex justify-between items-center backdrop-blur-md transition-all duration-300 ${
          scrolled ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div>
          <span className="text-[10px] text-[#E5A93C] font-bold uppercase tracking-wider block">Special Offer</span>
          <span className="font-serif-display text-sm font-bold text-[#F4F1EA]">₹999/-</span>
        </div>
        <button
          onClick={openEnrollModal}
          className="px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] active:scale-95 transition-all"
        >
          Enroll Now
        </button>
      </div>

      {/* INTERACTIVE ENROLLMENT MODAL */}
      {isModalOpen && (
        <div 
          data-testid="enrollment-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-opacity"
        >
          <div className="bg-[#132238] rounded-2xl border-2 border-[#E5A93C] max-w-md w-full overflow-hidden shadow-2xl relative animate-scale-in">
            
            {/* Close btn */}
            <button 
              onClick={closeEnrollModal}
              className="absolute top-4 right-4 text-[#A3B8B0] hover:text-[#E5A93C] p-1 focus:outline-none"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>

            {!formSubmitted ? (
              <form onSubmit={handleFormSubmit} className="p-6 sm:p-8 space-y-5 text-left">
                <div className="text-center space-y-2">
                  <div className="h-10 w-14 bg-[#E5A93C]/10 border border-[#E5A93C]/30 rounded-xl flex items-center justify-center mx-auto">
                    <Sparkles className="h-5 w-5 text-[#E5A93C]" />
                  </div>
                  <h3 className="font-serif-display text-xl sm:text-2xl font-bold text-[#F4F1EA]">
                    Begin Your Journey
                  </h3>
                  <p className="text-xs text-[#A3B8B0]">
                    Register for Sneha's 30-Day transformation and get daily WhatsApp delivery logs.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Name field */}
                  <div>
                    <label htmlFor="modal-name" className="block text-xs font-bold uppercase text-[#E5A93C] tracking-wide mb-1.5">
                      Your Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                        <User className="h-4 w-4" />
                      </div>
                      <input 
                        type="text"
                        id="modal-name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        data-testid="modal-input-name"
                        placeholder="e.g. Sneha Sharma"
                        className="w-full bg-[#0A111F] border border-[#E5A93C]/20 rounded-xl py-3 pl-10 pr-4 text-sm text-[#F4F1EA] placeholder-gray-600 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div>
                    <label htmlFor="modal-email" className="block text-xs font-bold uppercase text-[#E5A93C] tracking-wide mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                        <Mail className="h-4 w-4" />
                      </div>
                      <input 
                        type="email"
                        id="modal-email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        data-testid="modal-input-email"
                        placeholder="e.g. email@example.com"
                        className="w-full bg-[#0A111F] border border-[#E5A93C]/20 rounded-xl py-3 pl-10 pr-4 text-sm text-[#F4F1EA] placeholder-gray-600 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* WhatsApp field */}
                  <div>
                    <label htmlFor="modal-whatsapp" className="block text-xs font-bold uppercase text-[#E5A93C] tracking-wide mb-1.5">
                      WhatsApp Number (for delivery)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                        <Smartphone className="h-4 w-4" />
                      </div>
                      <input 
                        type="tel"
                        id="modal-whatsapp"
                        name="whatsapp"
                        required
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        data-testid="modal-input-phone"
                        placeholder="e.g. +91 98765 43210"
                        className="w-full bg-[#0A111F] border border-[#E5A93C]/20 rounded-xl py-3 pl-10 pr-4 text-sm text-[#F4F1EA] placeholder-gray-600 focus:border-[#E5A93C] focus:ring-1 focus:ring-[#E5A93C] focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Agreement checkbox */}
                  <div className="flex items-start space-x-3 pt-1">
                    <input 
                      type="checkbox"
                      id="modal-agree"
                      name="agreeTime"
                      required
                      checked={formData.agreeTime}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-gray-300 text-[#E5A93C] focus:ring-[#E5A93C] mt-0.5"
                    />
                    <label htmlFor="modal-agree" className="text-xs text-[#A3B8B0] select-none leading-relaxed">
                      I commit to dedicating 20–25 minutes a day for the next 30 days to my personal growth and wellbeing.
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#E5A93C]/10 mt-2">
                  <div className="flex justify-between items-center mb-4 text-xs">
                    <span className="text-[#A3B8B0]">30 Days Access:</span>
                    <span className="font-bold text-[#E5A93C]">₹999/-</span>
                  </div>
                  <button
                    type="submit"
                    data-testid="modal-submit-btn"
                    className="w-full py-3.5 rounded-full font-bold text-sm uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-all"
                  >
                    Yes, I Commit • Proceed to Pay
                  </button>
                </div>
              </form>
            ) : (
              <div 
                data-testid="modal-success-screen"
                className="p-8 text-center space-y-6"
              >
                <div className="h-16 w-16 bg-[#3B82F6]/15 rounded-full border border-[#3B82F6]/30 flex items-center justify-center mx-auto">
                  <Check className="h-8 w-8 text-[#3B82F6]" />
                </div>

                <div className="space-y-2">
                  <h3 className="font-serif-display text-2xl font-bold text-[#F4F1EA]">
                    Welcome to the Circle! 🎉
                  </h3>
                  <p className="text-sm text-[#A3B8B0]">
                    Your details are registered successfully.
                  </p>
                  <p className="text-xs font-semibold text-[#E5A93C] animate-pulse">
                    Redirecting to secure payment checkout in {redirectTimer} seconds...
                  </p>
                </div>

                <div className="bg-[#0A111F] rounded-xl p-4 border border-[#E5A93C]/10 text-xs text-[#D1CFC7] space-y-2 text-left">
                  <p>
                    <strong>Hi {formData.name},</strong>
                  </p>
                  <p>
                    Coaching team registered email <code>{formData.email}</code>. 
                  </p>
                  <p>
                    Onboarding logs and links will ship to WhatsApp <strong>{formData.whatsapp}</strong> shortly.
                  </p>
                </div>

                <button
                  onClick={() => {
                    window.location.href = CHECKOUT_URL;
                  }}
                  className="w-full py-3.5 rounded-full font-bold text-xs uppercase tracking-wider text-[#0A111F] bg-[#E5A93C] hover:bg-[#F5B84C] transition-all flex items-center justify-center space-x-2 shadow-lg shadow-[#E5A93C]/10"
                >
                  <span>Proceed to Payment Now (₹999)</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}

// Simple fallback Icon since Lucide react is imported
function LeafIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 3.5 1 9.8a7 7 0 0 1-9 8.2Z" />
      <path d="M9.8 6.1C11.5 10 13.5 12 19 11.8" />
    </svg>
  );
}
